/**
 * REPL prompt for the AI Sandbox debug page.
 *
 * Behaviour:
 *   - Enter           → submit (run the command)
 *   - Shift+Enter     → newline (default textarea behaviour, then resize)
 *   - Cmd/Ctrl + A    → select all (do not bubble up to the page)
 *   - ArrowUp         → previous command in history (only when cursor is on first line)
 *   - ArrowDown       → next command in history; back to the draft when past the newest
 *   - Auto-grows the textarea up to a cap, then scrolls inside.
 *
 * History is kept in-memory per LiveView lifetime (i.e. per browser tab). The
 * current draft is preserved when you start scrolling back, and restored when
 * you scroll forward past the newest entry.
 *
 * Pairs with `SauronWeb.Admin.AI.SandboxLive.Index`.
 */
const MAX_HEIGHT = 200;
const MAX_HISTORY = 500;

export const SandboxPrompt = {
  mounted() {
    this.history = [];
    this.historyIdx = null;
    this.draft = "";

    this.el.addEventListener("keydown", (e) => this.onKeyDown(e));
    this.el.addEventListener("input", () => this.onInput());

    this.handleEvent("focus-prompt", () => this.el.focus());

    this.resize();
    if (this.el.hasAttribute("autofocus")) {
      this.el.focus();
    }
  },

  updated() {
    this.resize();
  },

  onKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "a") {
      e.stopPropagation();
      this.el.select();
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const value = this.el.value;
      if (value.trim() !== "") {
        this.pushHistory(value);
        this.el.form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      }
      setTimeout(() => this.resize(), 0);
      return;
    }

    if (e.key === "ArrowUp" && this.cursorOnFirstLine()) {
      if (this.history.length === 0) return;
      e.preventDefault();
      if (this.historyIdx === null) {
        this.draft = this.el.value;
        this.historyIdx = this.history.length - 1;
      } else if (this.historyIdx > 0) {
        this.historyIdx -= 1;
      }
      this.setValue(this.history[this.historyIdx]);
      return;
    }

    if (e.key === "ArrowDown" && this.cursorOnLastLine()) {
      if (this.historyIdx === null) return;
      e.preventDefault();
      if (this.historyIdx < this.history.length - 1) {
        this.historyIdx += 1;
        this.setValue(this.history[this.historyIdx]);
      } else {
        this.historyIdx = null;
        this.setValue(this.draft);
        this.draft = "";
      }
      return;
    }
  },

  onInput() {
    // Editing breaks history-browse mode and resets the draft tracking.
    if (this.historyIdx !== null) {
      this.historyIdx = null;
      this.draft = "";
    }
    this.resize();
  },

  pushHistory(value) {
    const trimmed = value.replace(/\s+$/, "");
    if (trimmed === "") return;
    if (this.history[this.history.length - 1] !== trimmed) {
      this.history.push(trimmed);
      if (this.history.length > MAX_HISTORY) {
        this.history.splice(0, this.history.length - MAX_HISTORY);
      }
    }
    this.historyIdx = null;
    this.draft = "";
  },

  setValue(v) {
    this.el.value = v;
    this.el.setSelectionRange(v.length, v.length);
    // Keep server-side @script in sync via phx-change.
    this.el.dispatchEvent(new Event("input", { bubbles: true }));
    this.resize();
  },

  cursorOnFirstLine() {
    const pos = this.el.selectionStart;
    return !this.el.value.substring(0, pos).includes("\n");
  },

  cursorOnLastLine() {
    const pos = this.el.selectionEnd;
    return !this.el.value.substring(pos).includes("\n");
  },

  resize() {
    this.el.style.height = "auto";
    const scrollHeight = this.el.scrollHeight;
    const newHeight = Math.min(scrollHeight, MAX_HEIGHT);
    this.el.style.height = newHeight + "px";
    this.el.style.overflowY = scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  },
};
